// Chakra imports
import {
  Box,
  Text,
  useColorModeValue,
  Flex,
  Spinner,
  Alert,
  AlertIcon,
  Card,
  Divider,
  Icon,
  Button,
  Badge,
  useToast,
} from "@chakra-ui/react";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MdNotifications, MdReport, MdCheckCircle } from "react-icons/md";
import { formatDate } from "utils/format";
import supportService from "services/supportService";

export default function Notifications() {
  const navigate = useNavigate();
  const toast = useToast();

  const textColor = useColorModeValue("secondaryGray.900", "white");
  const textColorSecondary = useColorModeValue("secondaryGray.600", "white");
  const cardBg = useColorModeValue("white", "navy.800");
  const borderColor = useColorModeValue("secondaryGray.200", "whiteAlpha.100");
  const iconBoxBg = useColorModeValue("brand.50", "whiteAlpha.100");
  const rowBorder = useColorModeValue("gray.100", "whiteAlpha.100");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [pagination, setPagination] = useState({ skip: 0, limit: 50, total: 0, count: 0 });
  const [updatingId, setUpdatingId] = useState(null);

  const fetchNotifications = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await supportService.getNotifications({ skip: 0, limit: 50 });
      if (result.success && result.data) {
        setNotifications(result.data);
        if (result.pagination) {
          setPagination({
            skip: result.pagination.skip,
            limit: result.pagination.limit,
            total: result.pagination.total,
            count: result.pagination.count,
          });
        }
      } else {
        setError(result.error || "Failed to load notifications");
      }
    } catch (err) {
      console.error("Error fetching notifications:", err);
      setError("Failed to load notifications. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const handleMarkSpam = async (notification) => {
    const newSpam = !notification.marked_spam;
    setUpdatingId(notification.id);
    try {
      const result = await supportService.markNotificationSpam(notification.id, newSpam);
      if (result.success) {
        setNotifications((prev) =>
          prev.map((n) => (n.id === notification.id ? { ...n, marked_spam: newSpam } : n))
        );
        toast({
          title: "Success",
          description: result.message || (newSpam ? "Marked as spam" : "Unmarked as spam"),
          status: "success",
          duration: 4000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Error",
          description: result.error || "Failed to update",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to update notification.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUpdatingId(null);
    }
  };

  const getTypeLabel = (type) => {
    if (!type) return type;
    return type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  if (loading) {
    return (
      <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
        <Box display="flex" justifyContent="center" alignItems="center" minH="400px">
          <Spinner size="xl" color="brand.500" />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
        <Alert status="error" borderRadius="15px">
          <AlertIcon />
          <Text color={textColor}>{error}</Text>
        </Alert>
      </Box>
    );
  }

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }} maxW="1000px" mx="auto">
      <Flex direction="column" gap="6">
        <Flex align="center" gap="3">
          <Flex
            w="12"
            h="12"
            borderRadius="xl"
            bg={iconBoxBg}
            align="center"
            justify="center"
          >
            <Icon as={MdNotifications} color="brand.500" boxSize="6" />
          </Flex>
          <Box>
            <Text color={textColor} fontSize="2xl" fontWeight="700">
              Notifications
            </Text>
            <Text color={textColorSecondary} fontSize="sm">
              {pagination.total} notification{pagination.total !== 1 ? "s" : ""}
            </Text>
          </Box>
        </Flex>

        <Card p="0" bg={cardBg} overflow="hidden" borderRadius="16px" boxShadow="sm">
          <Flex align="center" gap="3" px="6" py="4" borderBottomWidth="1px" borderColor={borderColor}>
            <Icon as={MdNotifications} color="brand.500" boxSize="5" />
            <Text color={textColor} fontSize="lg" fontWeight="600">
              Support notification emails
            </Text>
          </Flex>
          <Divider borderColor={borderColor} />

          {notifications && notifications.length > 0 ? (
            <Box py="2">
              {notifications.map((n) => (
                <Flex
                  key={n.id}
                  direction={{ base: "column", md: "row" }}
                  align={{ base: "stretch", md: "center" }}
                  gap="3"
                  px="6"
                  py="4"
                  borderBottomWidth="1px"
                  borderColor={rowBorder}
                  _last={{ borderBottom: "none" }}
                >
                  <Box flex="1" minW="0">
                    <Flex align="center" gap="2" flexWrap="wrap" mb="1">
                      <Badge
                        colorScheme={n.marked_spam ? "red" : "blue"}
                        variant="subtle"
                        fontSize="xs"
                      >
                        {getTypeLabel(n.type)}
                      </Badge>
                      {n.marked_spam && (
                        <Badge colorScheme="red" fontSize="xs">
                          Spam
                        </Badge>
                      )}
                    </Flex>
                    <Text color={textColor} fontSize="md" fontWeight="600" noOfLines={1}>
                      {n.subject}
                    </Text>
                    <Text color={textColorSecondary} fontSize="sm" mt="0.5">
                      Visitor: {n.visitor_email || "—"}
                    </Text>
                    {n.recipients && n.recipients.length > 0 && (
                      <Text color={textColorSecondary} fontSize="xs" mt="0.5">
                        To: {n.recipients.join(", ")}
                      </Text>
                    )}
                    <Text color={textColorSecondary} fontSize="xs" mt="1">
                      {n.sent_at ? formatDate(n.sent_at) : n.created_at ? formatDate(n.created_at) : "—"}
                    </Text>
                  </Box>
                  <Flex shrink={0} gap="2" align="center">
                    {n.related_conversation_id && (
                      <Button
                        size="sm"
                        variant="outline"
                        colorScheme="blue"
                        onClick={() => navigate(`/admin/support/conversation/${n.related_conversation_id}`)}
                      >
                        View chat
                      </Button>
                    )}
                    <Button
                      size="sm"
                      leftIcon={<Icon as={n.marked_spam ? MdCheckCircle : MdReport} />}
                      variant={n.marked_spam ? "outline" : "solid"}
                      colorScheme={n.marked_spam ? "green" : "red"}
                      onClick={() => handleMarkSpam(n)}
                      isLoading={updatingId === n.id}
                      loadingText="..."
                    >
                      {n.marked_spam ? "Unmark spam" : "Mark as spam"}
                    </Button>
                  </Flex>
                </Flex>
              ))}
            </Box>
          ) : (
            <Flex direction="column" align="center" justify="center" py="16" px="6">
              <Text color={textColorSecondary} fontSize="sm" textAlign="center">
                No notifications yet.
              </Text>
            </Flex>
          )}
        </Card>
      </Flex>
    </Box>
  );
}
